class KenraOurStoryTimeline extends HTMLElement {
  constructor() {
    super();
    this.tabs = Array.from(this.querySelectorAll('.kenra-our-story-timeline__tab'));
    this.slides = Array.from(this.querySelectorAll('.kenra-our-story-timeline__slide'));
    this.slideButtons = Array.from(this.querySelectorAll('.kenra-our-story-timeline__slide-button'));
    this.highlights = Array.from(this.querySelectorAll('.kenra-our-story-timeline__highlight'));
    this.slider = this.querySelector('.kenra-our-story-timeline__slider');
    this.navPrev = this.querySelector('.kenra-our-story-timeline__nav--prev');
    this.navNext = this.querySelector('.kenra-our-story-timeline__nav--next');
    this.activeSlideIndex = 0;
    this.programmaticScrollUntil = 0;

    this.onTabClick = this.onTabClick.bind(this);
    this.onTabKeydown = this.onTabKeydown.bind(this);
    this.onSlideClick = this.onSlideClick.bind(this);
    this.onSlideKeydown = this.onSlideKeydown.bind(this);
    this.onSliderScroll = this.onSliderScroll.bind(this);
    this.onNavClick = this.onNavClick.bind(this);
  }

  connectedCallback() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick);
      tab.addEventListener('keydown', this.onTabKeydown);
    });
    this.slideButtons.forEach((btn) => {
      btn.addEventListener('click', this.onSlideClick);
      btn.addEventListener('keydown', this.onSlideKeydown);
    });
    if (this.slider) {
      this.slider.addEventListener('scroll', this.onSliderScroll, { passive: true });
    }
    if (this.navPrev) this.navPrev.addEventListener('click', this.onNavClick);
    if (this.navNext) this.navNext.addEventListener('click', this.onNavClick);
    this.setActiveSlide(0, { scroll: false });
  }

  disconnectedCallback() {
    this.tabs.forEach((tab) => {
      tab.removeEventListener('click', this.onTabClick);
      tab.removeEventListener('keydown', this.onTabKeydown);
    });
    this.slideButtons.forEach((btn) => {
      btn.removeEventListener('click', this.onSlideClick);
      btn.removeEventListener('keydown', this.onSlideKeydown);
    });
    if (this.slider) {
      this.slider.removeEventListener('scroll', this.onSliderScroll);
    }
    if (this.navPrev) this.navPrev.removeEventListener('click', this.onNavClick);
    if (this.navNext) this.navNext.removeEventListener('click', this.onNavClick);
  }

  onNavClick(event) {
    event.preventDefault();
    const direction = event.currentTarget.dataset.direction === 'next' ? 1 : -1;
    const next = this.activeSlideIndex + direction;
    if (next < 0 || next >= this.slides.length) return;
    this.setActiveSlide(next, { scroll: true });
  }

  onTabClick(event) {
    event.preventDefault();
    const idx = parseInt(event.currentTarget.dataset.firstSlideIndex, 10);
    if (!Number.isNaN(idx) && idx >= 0) this.setActiveSlide(idx, { scroll: true });
  }

  onTabKeydown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    const nextIndex = (currentIndex + direction + this.tabs.length) % this.tabs.length;
    const nextTab = this.tabs[nextIndex];
    nextTab.focus();
    const idx = parseInt(nextTab.dataset.firstSlideIndex, 10);
    if (!Number.isNaN(idx) && idx >= 0) this.setActiveSlide(idx, { scroll: true });
  }

  onSlideClick(event) {
    event.preventDefault();
    const idx = parseInt(event.currentTarget.dataset.slideIndex, 10);
    if (!Number.isNaN(idx)) this.setActiveSlide(idx, { scroll: true });
  }

  onSlideKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    event.preventDefault();
    const idx = parseInt(event.currentTarget.dataset.slideIndex, 10);
    if (!Number.isNaN(idx)) this.setActiveSlide(idx, { scroll: true });
  }

  onSliderScroll() {
    if (!this.slider || this.slides.length === 0) return;
    if (performance.now() < this.programmaticScrollUntil) return;
    const containerRect = this.slider.getBoundingClientRect();
    const containerLeft = containerRect.left;
    let closest = 0;
    let closestDistance = Infinity;
    this.slides.forEach((slide, i) => {
      const r = slide.getBoundingClientRect();
      const distance = Math.abs(r.left - containerLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = i;
      }
    });
    if (closest !== this.activeSlideIndex) {
      this.setActiveSlide(closest, { scroll: false });
    }
  }

  setActiveSlide(index, { scroll }) {
    if (index < 0 || index >= this.slides.length) return;
    this.activeSlideIndex = index;
    const activeSlide = this.slides[index];
    const activeEra = activeSlide ? activeSlide.dataset.era : '';

    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
    });

    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.era === activeEra;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.highlights.forEach((h) => {
      const slideIndexAttr = parseInt(h.dataset.slideIndex, 10);
      if (slideIndexAttr === index) {
        h.removeAttribute('hidden');
      } else {
        h.setAttribute('hidden', '');
      }
    });

    if (this.navPrev) {
      const disabled = index <= 0;
      this.navPrev.classList.toggle('is-disabled', disabled);
      this.navPrev.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      this.navPrev.tabIndex = disabled ? -1 : 0;
    }
    if (this.navNext) {
      const disabled = index >= this.slides.length - 1;
      this.navNext.classList.toggle('is-disabled', disabled);
      this.navNext.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      this.navNext.tabIndex = disabled ? -1 : 0;
    }

    if (scroll && this.slider && activeSlide) {
      this.programmaticScrollUntil = performance.now() + 800;
      this.slider.scrollTo({ left: activeSlide.offsetLeft, behavior: 'smooth' });
    }
  }
}

if (!customElements.get('kenra-our-story-timeline')) {
  customElements.define('kenra-our-story-timeline', KenraOurStoryTimeline);
}
